import * as i0 from '@angular/core';
import { EventEmitter, HostListener, Output, Input, ViewEncapsulation, Component } from '@angular/core';
import { htmlId } from '@engineering11/utility';
import * as echarts from 'echarts';

/**
 * A versatile chart component.
 * Supports multiple chart types including line, bar, pie, donut, and scatter charts.
 *
 * @example
 * <e11-chart
 *   [type]="'line'"
 *   [series]="[{name: 'Sales', data: [{name: 'Jan', value: 100}, {name: 'Feb', value: 200}]}]"
 *   [title]="'Monthly Sales'"
 * >
 * </e11-chart>
 */
class E11ChartComponent {
    constructor() {
        /** Unique identifier for the chart container. Auto-generated if not provided. */
        this.chartId = htmlId('e11-chart');
        /** The type of chart to render. */
        this.type = 'line';
        /** Array of data series to be displayed in the chart. */
        this.series = [];
        /** Width of the chart container. Accepts CSS values. */
        this.width = '100%';
        /** Height of the chart container. In px. */
        this.height = 400;
        /** Whether to show the chart legend. */
        this.showLegend = true;
        /** Position of the legend relative to the chart. */
        this.legendPosition = 'bottom';
        /** Orientation of the legend items. */
        this.legendOrient = 'horizontal';
        /** Distance of the legend from the specified position edge. Accepts CSS values. */
        this.legendDistance = '5%';
        /** Whether to show the label on mobile. */
        this.showLabelOnMobile = false;
        /** Array of data for the X-axis */
        this.xAxisData = undefined;
        /** Zoom end value for line and scatter charts. */
        this.zoomEnd = 100;
        /** Whether to show the data zoom slide bar on line and scatter charts. */
        this.showDataZoom = false;
        /** Gradient configuration for line chart. Only applicable for line charts. */
        this.lineGradient = {
            startColor: 'rgb(255, 158, 68)',
            endColor: 'rgb(255, 70, 131)',
        };
        /** Height of inline-bar chart type in pixels. Only applicable when type is 'inline-bar'. */
        this.inlineBarHeight = 50;
        /** Events propogated from echarts component */
        this.chartClick = new EventEmitter();
        this.chartDblClick = new EventEmitter();
        this.chartMouseDown = new EventEmitter();
        this.chartMouseMove = new EventEmitter();
        this.chartMouseUp = new EventEmitter();
        this.chartMouseOver = new EventEmitter();
        this.chartMouseOut = new EventEmitter();
        this.chartGlobalOut = new EventEmitter();
        this.chartContextMenu = new EventEmitter();
        this.chartOutsideClick = new EventEmitter();
        /** Map used for binding events to echart component */
        this.eventEmitterMap = {
            click: this.chartClick,
            dblclick: this.chartDblClick,
            mousedown: this.chartMouseDown,
            mousemove: this.chartMouseMove,
            mouseup: this.chartMouseUp,
            mouseover: this.chartMouseOver,
            mouseout: this.chartMouseOut,
            globalout: this.chartGlobalOut,
            contextmenu: this.chartContextMenu,
        };
        this.chart = null;
        this.observer = null;
        this.initialWidth = 0;
        this._loading = false;
    }
    /** Controls whether or not to display loader for chart */
    set loading(loading) {
        this._loading = loading;
        if (!this.chart)
            return;
        loading ? this.chart.showLoading() : this.chart.hideLoading();
    }
    onResize(event) {
        if (this.chart) {
            this.chart.resize();
            this.updateChartOptions();
        }
    }
    ngAfterViewInit() {
        this.initChart();
    }
    initChart() {
        const chartDom = document.getElementById(this.chartId);
        if (!chartDom)
            return;
        this.chart = echarts.init(chartDom, 'light', { renderer: 'svg', height: this.height });
        this.bindChartEvents();
        this.updateChartOptions();
        this.initialWidth = this.chart.getDom().clientWidth;
        this.setupChartResizeObserver();
        if (this._loading)
            this.chart.showLoading();
    }
    /**
     * Setup a resize observer to listen for changes in the chart's parent element.
     * This is used to trigger a resize event when used in a tab.
     */
    setupChartResizeObserver() {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const currentWidth = this.chart?.getDom().clientWidth;
                if (entry.isIntersecting && this.chart && (this.initialWidth === 0 || this.initialWidth !== currentWidth)) {
                    this.initialWidth = currentWidth;
                    this.onResize(null);
                }
            });
        });
        const chartElement = document.getElementById(this.chartId);
        if (chartElement) {
            this.observer.observe(chartElement);
        }
    }
    getTransformedType() {
        switch (this.type) {
            case 'inline-bar':
                return 'bar';
            case 'donut':
                return 'pie';
            default:
                return this.type;
        }
    }
    getSeriesConfig(series) {
        const baseConfig = {
            name: series.name,
            type: this.getTransformedType(),
            itemStyle: {
                color: series.color,
            },
            data: series.data.map((item) => ({
                value: item.value,
                name: item.name,
                itemStyle: item.color ? { color: item.color } : undefined,
            })),
        };
        switch (this.type) {
            case 'pie':
            case 'donut':
                return {
                    ...baseConfig,
                    radius: ['40%', '70%'],
                    startAngle: this.type === 'donut' ? 180 : 0,
                    endAngle: this.type === 'donut' ? 360 : undefined,
                    avoidLabelOverlap: true,
                    center: this.type === 'donut' ? ['50%', '70%'] : ['50%', '50%'],
                    itemStyle: {
                        borderRadius: this.type === 'donut' ? 0 : 10,
                        borderWidth: this.type === 'donut' ? 0 : 2,
                        borderColor: this.type === 'donut' ? 'transparent' : '#fff',
                    },
                    label: {
                        show: this.type === 'donut' ? true : false,
                        position: this.type === 'donut' ? 'outside' : 'center',
                        fontWeight: 'bold',
                        formatter: '{b}: {c}',
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: 16,
                            fontWeight: 'bold',
                        },
                    },
                };
            case 'bar':
            case 'inline-bar':
                return {
                    ...baseConfig,
                    barMaxWidth: 50,
                    stack: this.type === 'inline-bar' ? 'total' : undefined,
                    emphasis: {
                        focus: 'series',
                    },
                };
            case 'line':
                return {
                    ...baseConfig,
                    smooth: true,
                    symbol: 'none',
                    emphasis: {
                        focus: 'series',
                        scale: 1.5,
                    },
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            {
                                offset: 0,
                                color: this.lineGradient.startColor,
                            },
                            {
                                offset: 1,
                                color: this.lineGradient.endColor,
                            },
                        ]),
                    },
                };
            case 'scatter':
                return {
                    ...baseConfig,
                    symbolSize: 12,
                    emphasis: {
                        focus: 'series',
                        scale: 1.5,
                    },
                };
            default:
                return baseConfig;
        }
    }
    bindChartEvents() {
        Object.entries(this.eventEmitterMap).forEach(([event, emitter]) => {
            this.chart.on(event, (event) => emitter.emit(event));
        });
        this.chart.getZr().on('click', (event) => {
            if (!event.target)
                this.chartOutsideClick.emit(event);
        });
    }
    removeChartEvents() {
        Object.entries(this.eventEmitterMap).forEach(([event, emitter]) => {
            this.chart.off(event);
        });
        this.chart.getZr().off('click');
    }
    updateChartOptions() {
        if (!this.chart)
            return;
        const options = {
            title: this.title
                ? {
                    text: this.title,
                    subtext: this.subtitle,
                    left: 'center',
                }
                : undefined,
            tooltip: {
                trigger: ['pie', 'donut'].includes(this.type) ? 'item' : 'axis',
                formatter: ['pie', 'donut'].includes(this.type) ? '<b>{a}</b> <br/>{b}: <b>{c} ({d}%)</b>' : undefined,
                axisPointer: this.type === 'inline-bar' ? { type: 'shadow' } : undefined,
            },
            height: this.type === 'inline-bar' ? this.inlineBarHeight : 'auto',
            legend: this.showLegend
                ? {
                    orient: this.legendOrient,
                    [this.legendPosition]: this.legendDistance,
                    textStyle: {
                        fontWeight: 'bold',
                    },
                }
                : undefined,
            xAxis: this.getXAxisConfig(),
            yAxis: this.getYAxisConfig(),
            dataZoom: ['line', 'scatter'].includes(this.type)
                ? [
                    {
                        type: 'inside',
                        start: 0,
                        end: this.zoomEnd,
                    },
                    this.showDataZoom
                        ? {
                            start: 0,
                            end: this.zoomEnd,
                        }
                        : null,
                ]
                : undefined,
            series: this.series.map((s) => this.getSeriesConfig(s)),
            animation: true,
            media: [
                {
                    query: { maxWidth: 600 },
                    option: {
                        series: [
                            {
                                label: {
                                    show: this.showLabelOnMobile,
                                },
                                emphasis: {
                                    label: {
                                        show: this.showLabelOnMobile,
                                    },
                                },
                            },
                        ],
                    },
                },
            ],
        };
        this.chart.setOption(options);
    }
    getXAxisConfig() {
        if (['pie', 'donut'].includes(this.type))
            return undefined;
        if (this.type === 'inline-bar') {
            return {
                type: 'value',
                show: false,
                name: this.xAxisName,
            };
        }
        return {
            type: 'category',
            name: this.xAxisName,
            boundaryGap: this.type === 'line' ? false : undefined,
            data: this.type === 'line' || this.type === 'bar' ? this.xAxisData : undefined,
        };
    }
    getYAxisConfig() {
        if (['pie', 'donut'].includes(this.type))
            return undefined;
        if (this.type === 'inline-bar') {
            return {
                show: false,
                type: 'category',
                data: [''],
            };
        }
        return {
            type: 'value',
            name: this.yAxisName,
            boundaryGap: this.type === 'line' ? [0, '100%'] : undefined,
        };
    }
    ngOnChanges() {
        this.updateChartOptions();
    }
    ngOnDestroy() {
        if (this.chart) {
            this.removeChartEvents();
            this.chart.dispose();
            this.observer?.disconnect();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ChartComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11ChartComponent, isStandalone: true, selector: "e11-chart", inputs: { chartId: "chartId", type: "type", series: "series", width: "width", height: "height", title: "title", subtitle: "subtitle", showLegend: "showLegend", yAxisName: "yAxisName", xAxisName: "xAxisName", legendPosition: "legendPosition", legendOrient: "legendOrient", legendDistance: "legendDistance", showLabelOnMobile: "showLabelOnMobile", xAxisData: "xAxisData", zoomEnd: "zoomEnd", showDataZoom: "showDataZoom", lineGradient: "lineGradient", inlineBarHeight: "inlineBarHeight", loading: "loading" }, outputs: { chartClick: "chartClick", chartDblClick: "chartDblClick", chartMouseDown: "chartMouseDown", chartMouseMove: "chartMouseMove", chartMouseUp: "chartMouseUp", chartMouseOver: "chartMouseOver", chartMouseOut: "chartMouseOut", chartGlobalOut: "chartGlobalOut", chartContextMenu: "chartContextMenu", chartOutsideClick: "chartOutsideClick" }, host: { listeners: { "window:resize": "onResize($event)" } }, usesOnChanges: true, ngImport: i0, template: ` <div [id]="chartId" [style.width]="width"></div> `, isInline: true, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ChartComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-chart',
                    standalone: true,
                    template: ` <div [id]="chartId" [style.width]="width"></div> `,
                    encapsulation: ViewEncapsulation.None,
                }]
        }], propDecorators: { chartId: [{
                type: Input
            }], type: [{
                type: Input
            }], series: [{
                type: Input
            }], width: [{
                type: Input
            }], height: [{
                type: Input
            }], title: [{
                type: Input
            }], subtitle: [{
                type: Input
            }], showLegend: [{
                type: Input
            }], yAxisName: [{
                type: Input
            }], xAxisName: [{
                type: Input
            }], legendPosition: [{
                type: Input
            }], legendOrient: [{
                type: Input
            }], legendDistance: [{
                type: Input
            }], showLabelOnMobile: [{
                type: Input
            }], xAxisData: [{
                type: Input
            }], zoomEnd: [{
                type: Input
            }], showDataZoom: [{
                type: Input
            }], lineGradient: [{
                type: Input
            }], inlineBarHeight: [{
                type: Input
            }], loading: [{
                type: Input
            }], chartClick: [{
                type: Output
            }], chartDblClick: [{
                type: Output
            }], chartMouseDown: [{
                type: Output
            }], chartMouseMove: [{
                type: Output
            }], chartMouseUp: [{
                type: Output
            }], chartMouseOver: [{
                type: Output
            }], chartMouseOut: [{
                type: Output
            }], chartGlobalOut: [{
                type: Output
            }], chartContextMenu: [{
                type: Output
            }], chartOutsideClick: [{
                type: Output
            }], onResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { E11ChartComponent };
